import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from '../common/SearchableSelect';
import productCatalogueService from '../../services/productCatalogueService';
import { useWarehouseContext } from '../../context/WarehouseContext';
import { UI_COLORS } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

/**
 * Normalize the various shapes the catalogue endpoints may return:
 * a plain array, { data: [...] }, { result: [...] }, or { items/products: [...] }.
 */
const extractArray = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.result)) return res.result;
  if (res.data && Array.isArray(res.data.items)) return res.data.items;
  if (res.data && Array.isArray(res.data.products)) return res.data.products;
  if (Array.isArray(res.items)) return res.items;
  if (Array.isArray(res.products)) return res.products;
  return [];
};

// `t` is passed in from the component (react-i18next hook) since module-scope
// helpers cannot call the hook directly.
const subCategoryLabel = (sc, t) =>
  sc.product_name || sc.name || sc.alias_name ||
  t('warehouseInventory.exportBySubcategory.subCategoryFallback', { id: sc.id });

/**
 * WarehouseInventoryExportModal
 * SUPER_ADMIN-only modal to export inventory as a CSV pivoted product-row ×
 * warehouse-column, filtered by one or more Sub Categories (ProductCatalogue).
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onExport: ({ catalogue_ids, warehouse_ids, value }) => Promise<{success, error}>
 * - loading: boolean (export in-flight)
 */
const WarehouseInventoryExportModal = ({ isOpen, onClose, onExport, loading = false }) => {
  const { t } = useTranslation();
  const exportStrings = t('warehouseInventory.exportBySubcategory', { returnObjects: true });
  const { warehouses, loading: warehousesLoading } = useWarehouseContext();

  // Sub category search results (server-side) + a running map of every
  // sub category we've ever seen (by id), so selected items keep their labels
  // and never disappear from the list when the search term changes.
  const [searchResults, setSearchResults] = useState([]);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);
  const [selectedCatalogueIds, setSelectedCatalogueIds] = useState([]);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState([]);
  const [value, setValue] = useState('stock');

  // id -> { value, label } for anything we've fetched; persists across searches.
  const knownByIdRef = useRef(new Map());
  const searchDebounceRef = useRef(null);
  const searchSeqRef = useRef(0);

  const rememberSubCategories = useCallback((list) => {
    list.forEach((sc) => {
      if (sc && sc.id != null) {
        knownByIdRef.current.set(String(sc.id), { value: sc.id, label: subCategoryLabel(sc, t) });
      }
    });
  }, [t]);

  const fetchSubCategories = useCallback(async (search = '') => {
    const seq = ++searchSeqRef.current;
    setSubCategoriesLoading(true);
    try {
      const result = await productCatalogueService.getProductCatalogue({
        search: search || undefined,
        limit: 100,
      });
      // Ignore out-of-order responses (an older search resolving after a newer one).
      if (seq !== searchSeqRef.current) return;
      const list = extractArray(result);
      rememberSubCategories(list);
      setSearchResults(list);
    } catch (e) {
      if (seq === searchSeqRef.current) setSearchResults([]);
    } finally {
      if (seq === searchSeqRef.current) setSubCategoriesLoading(false);
    }
  }, [rememberSubCategories]);

  // Initial load when the modal opens
  useEffect(() => {
    if (!isOpen) return undefined;
    fetchSubCategories('');
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [isOpen, fetchSubCategories]);

  // Debounced server-side search wired into SearchableSelect's onSearchChange
  const handleSubCategorySearch = useCallback((term) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchSubCategories((term || '').trim());
    }, 350);
  }, [fetchSubCategories]);

  // Options for the sub category select = current search results, plus every
  // selected item (so a selection stays visible/checked even when it's not in
  // the active search results). De-duped by id.
  const subCategoryOptions = useMemo(() => {
    const byId = new Map();
    searchResults.forEach((sc) => {
      if (sc && sc.id != null) byId.set(String(sc.id), { value: sc.id, label: subCategoryLabel(sc, t) });
    });
    selectedCatalogueIds.forEach((id) => {
      const key = String(id);
      if (!byId.has(key)) {
        const known = knownByIdRef.current.get(key);
        byId.set(key, known || { value: id, label: `#${id}` });
      }
    });
    return Array.from(byId.values());
  }, [searchResults, selectedCatalogueIds, t]);

  // The selected sub categories as {value,label} chips, so the user can always
  // see exactly what's selected regardless of the current search term.
  const selectedSubCategoryChips = useMemo(
    () => selectedCatalogueIds.map((id) => {
      const known = knownByIdRef.current.get(String(id));
      return { value: id, label: known ? known.label : `#${id}` };
    }),
    [selectedCatalogueIds]
  );

  const removeSubCategory = (id) =>
    setSelectedCatalogueIds((prev) => prev.filter((v) => String(v) !== String(id)));

  if (!isOpen) return null;

  const warehouseOptions = (Array.isArray(warehouses) ? warehouses : []).map((w) => ({
    value: w.id,
    label: w.name || t('warehouseInventory.exportBySubcategory.warehouseFallback', { id: w.id }),
  }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    onExport({
      catalogue_ids: selectedCatalogueIds,
      warehouse_ids: selectedWarehouseIds,
      value,
    });
  };

  const labelStyle = { color: UI_COLORS.FORMS.LABEL };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={loading ? undefined : onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{exportStrings.title}</h2>
            <p className="mt-1 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{exportStrings.subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faTimes} style={{ color: UI_COLORS.TEXT.SECONDARY }} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={labelStyle}>
              {exportStrings.subCategoryLabel}
              {selectedCatalogueIds.length > 0 && (
                <span className="ml-1 font-normal" style={{ color: UI_COLORS.TEXT.MUTED }}>
                  ({selectedCatalogueIds.length} {exportStrings.selectedSuffix})
                </span>
              )}
            </label>
            <SearchableSelect
              isMulti
              options={subCategoryOptions}
              value={selectedCatalogueIds}
              onChange={(e) => setSelectedCatalogueIds(e.target.value)}
              onSearchChange={handleSubCategorySearch}
              placeholder={exportStrings.subCategoryPlaceholder}
              loading={subCategoriesLoading}
              noOptionsMessage={subCategoriesLoading ? exportStrings.loadingSubCategories : exportStrings.noSubCategories}
            />

            {selectedSubCategoryChips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {selectedSubCategoryChips.map((chip) => (
                  <span
                    key={chip.value}
                    className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 border border-teal-300"
                  >
                    {chip.label}
                    <button
                      type="button"
                      onClick={() => removeSubCategory(chip.value)}
                      className="rounded-full text-teal-700 hover:bg-teal-200 hover:text-teal-900 w-4 h-4 flex items-center justify-center transition-colors"
                      aria-label={t('warehouseInventory.exportBySubcategory.removeChip', { label: chip.label })}
                    >
                      <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.65rem' }} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <p className="mt-1 text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>{exportStrings.subCategoryHint}</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={labelStyle}>{exportStrings.warehouseLabel}</label>
            <SearchableSelect
              isMulti
              options={warehouseOptions}
              value={selectedWarehouseIds}
              onChange={(e) => setSelectedWarehouseIds(e.target.value)}
              placeholder={exportStrings.warehousePlaceholder}
              loading={warehousesLoading}
            />
            <p className="mt-1 text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>{exportStrings.warehouseHint}</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={labelStyle}>{exportStrings.valueLabel}</label>
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border-2"
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.TEXT.BLACK || UI_COLORS.FORMS.INPUT.text,
              }}
            >
              <option value="stock">{exportStrings.valueStock}</option>
              <option value="available">{exportStrings.valueAvailable}</option>
            </select>
          </div>

          <div className="flex gap-3 mt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 rounded-xl border-2 font-semibold transition-all disabled:opacity-50"
              style={{ borderColor: UI_COLORS.BORDERS.MEDIUM, color: UI_COLORS.TEXT.PRIMARY }}
            >
              {exportStrings.cancel}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: UI_COLORS.STATUS.SUCCESS, color: '#fff' }}
            >
              <FontAwesomeIcon icon={loading ? faSpinner : faDownload} className={loading ? 'animate-spin' : ''} />
              {loading ? exportStrings.exporting : exportStrings.export}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WarehouseInventoryExportModal;
